import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Plus, CalendarDays, Calendar as CalendarIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import { cn } from '../lib/utils.js';
import type { OnsiteJob } from '../../../api/src/types/onsite-types.js';

type View = 'week' | 'month';

const STATUS_CARD: Record<string, string> = {
  new:        'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/30',
  scheduled:  'bg-blue-500/15 text-blue-300 border-blue-500/30',
  dispatched: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  en_route:   'bg-purple-500/15 text-purple-300 border-purple-500/30',
  on_site:    'bg-green-500/15 text-green-300 border-green-500/30',
  completed:  'bg-teal-500/15 text-teal-300 border-teal-500/30',
  invoiced:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  closed:     'bg-secondary text-muted-foreground border-border',
  cancelled:  'bg-red-500/15 text-red-300 border-red-500/30',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfWeek(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  r.setDate(r.getDate() - r.getDay());
  return r;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfMonth(d: Date): Date {
  const r = new Date(d);
  r.setDate(1);
  r.setHours(0, 0, 0, 0);
  return r;
}

function isoDay(d: Date): string {
  return d.toISOString().substring(0, 10);
}

function formatMonthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function CalendarPage() {
  const { orgId } = useAuth();
  const [view, setView] = useState<View>('week');
  const [cursor, setCursor] = useState<Date>(() => new Date());

  const { range, days } = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(cursor);
      const dayList = Array.from({ length: 7 }, (_, i) => addDays(start, i));
      return { range: { from: dayList[0], to: addDays(dayList[6], 1) }, days: dayList };
    }
    const first = startOfMonth(cursor);
    const gridStart = startOfWeek(first);
    const dayList: Date[] = [];
    for (let i = 0; i < 42; i++) dayList.push(addDays(gridStart, i));
    return { range: { from: gridStart, to: addDays(gridStart, 42) }, days: dayList };
  }, [cursor, view]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['calendar', 'jobs', orgId, view, isoDay(range.from), isoDay(range.to)],
    queryFn: () => onsiteApiFetch<{ jobs: OnsiteJob[] }>(
      `/api/v1/onsite/jobs?date_from=${isoDay(range.from)}&date_to=${isoDay(range.to)}`
    ),
    enabled: !!orgId,
  });

  const jobs = data?.jobs ?? [];

  const jobsByDay = useMemo(() => {
    const m = new Map<string, OnsiteJob[]>();
    for (const job of jobs) {
      const key = job.scheduled_start ? isoDay(new Date(job.scheduled_start)) : '';
      if (!key) continue;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(job);
    }
    return m;
  }, [jobs]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const title = view === 'week'
    ? `Week of ${days[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : formatMonthLabel(cursor);

  function shift(delta: number) {
    setCursor(c => view === 'week' ? addDays(c, 7 * delta) : new Date(c.getFullYear(), c.getMonth() + delta, 1));
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={() => shift(-1)}
              className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Previous"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCursor(new Date())}
              className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              Today
            </button>
            <button
              onClick={() => shift(1)}
              className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <h1 className="ml-2 text-lg font-semibold text-foreground">{title}</h1>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border bg-secondary p-0.5">
              <button
                onClick={() => setView('week')}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  view === 'week' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Week
              </button>
              <button
                onClick={() => setView('month')}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  view === 'month' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <CalendarIcon className="h-3.5 w-3.5" />
                Month
              </button>
            </div>
            <Link
              to="/jobs/new"
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              New Job
            </Link>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="border-b border-border bg-red-500/10 px-4 py-2 text-sm text-red-300">
          {(error as Error).message}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : view === 'week' ? (
          <WeekView days={days} jobsByDay={jobsByDay} today={today} />
        ) : (
          <MonthView days={days} jobsByDay={jobsByDay} today={today} cursor={cursor} />
        )}
      </div>
    </div>
  );
}

function WeekView({ days, jobsByDay, today }: {
  days: Date[]; jobsByDay: Map<string, OnsiteJob[]>; today: Date;
}) {
  return (
    <>
      {/* Mobile swipe hint */}
      <div className="md:hidden border-b border-border bg-card/50 px-4 py-2 text-xs text-muted-foreground">
        Tip: scroll horizontally to see all days
      </div>
      <div className="grid grid-cols-7 min-w-[900px]">
        {days.map(day => {
          const isToday = isSameDay(day, today);
          const key = isoDay(day);
          const dayJobs = jobsByDay.get(key) ?? [];
          return (
            <div
              key={key}
              className={cn(
                'border-r border-b border-border last:border-r-0 min-h-[70vh] p-2',
                isToday && 'bg-primary/5'
              )}
            >
              <div className={cn(
                'mb-2 flex items-baseline justify-between',
                isToday && 'text-primary'
              )}>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {WEEKDAYS[day.getDay()]}
                </span>
                <span className={cn(
                  'text-sm font-semibold',
                  isToday ? 'rounded-full bg-primary px-2 py-0.5 text-primary-foreground' : 'text-foreground'
                )}>
                  {day.getDate()}
                </span>
              </div>
              <div className="space-y-1.5">
                {dayJobs.map(job => <JobCard key={job.id} job={job} />)}
                {dayJobs.length === 0 && (
                  <p className="text-center text-xs text-muted-foreground/60 py-4">—</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function MonthView({ days, jobsByDay, today, cursor }: {
  days: Date[]; jobsByDay: Map<string, OnsiteJob[]>; today: Date; cursor: Date;
}) {
  const currentMonth = cursor.getMonth();
  return (
    <div>
      <div className="grid grid-cols-7 border-b border-border bg-card/50">
        {WEEKDAYS.map(w => (
          <div key={w} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map(day => {
          const isToday = isSameDay(day, today);
          const inMonth = day.getMonth() === currentMonth;
          const key = isoDay(day);
          const dayJobs = jobsByDay.get(key) ?? [];
          return (
            <div
              key={key}
              className={cn(
                'border-r border-b border-border min-h-[120px] p-1.5',
                !inMonth && 'bg-background/30',
                isToday && 'bg-primary/5'
              )}
            >
              <div className={cn(
                'mb-1 text-xs font-medium',
                !inMonth ? 'text-muted-foreground/50' : isToday ? 'text-primary' : 'text-foreground'
              )}>
                <span className={cn(
                  isToday && 'rounded-full bg-primary px-1.5 py-0.5 text-primary-foreground'
                )}>
                  {day.getDate()}
                </span>
              </div>
              <div className="space-y-1">
                {dayJobs.slice(0, 3).map(job => (
                  <JobCard key={job.id} job={job} compact />
                ))}
                {dayJobs.length > 3 && (
                  <p className="text-xs text-muted-foreground">+{dayJobs.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JobCard({ job, compact = false }: { job: OnsiteJob; compact?: boolean }) {
  const cls = STATUS_CARD[job.status] ?? STATUS_CARD.new;
  const t = job.scheduled_start ? new Date(job.scheduled_start) : null;
  const time = t ? t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';

  if (compact) {
    return (
      <Link
        to={`/jobs/${job.id}`}
        className={cn('block rounded border px-1.5 py-0.5 text-xs truncate transition-colors hover:opacity-80', cls)}
        title={`${job.title} · ${job.customer_name}`}
      >
        <span className="font-medium">{time}</span> {job.title}
      </Link>
    );
  }

  return (
    <Link
      to={`/jobs/${job.id}`}
      className={cn(
        'block rounded-lg border p-2 transition-colors hover:opacity-90',
        cls
      )}
    >
      <p className="text-xs font-semibold line-clamp-1">{job.title}</p>
      <p className="text-[11px] opacity-80 line-clamp-1">{job.customer_name}</p>
      <div className="mt-1 flex items-center justify-between text-[11px] opacity-80">
        <span>{time}</span>
        {job.assigned_tech_name && (
          <span className="truncate ml-1">{job.assigned_tech_name.split(' ')[0]}</span>
        )}
      </div>
    </Link>
  );
}
